import { createApi } from '@reduxjs/toolkit/query/react';
import { DEVICE_INFO_API_URL } from '../../config';
import { deviceInfoAxiosQuery } from '../../utils/axios/deviceInfo.axios';
import type { EnhancedNarrativeRequest } from '../../interfaces/reports-tab/enhanced-narrative';
import {
    IAccountManagement,
    IAccountManagementDetails,
} from '../../interfaces/account/IAccountChatbox';
import { IOverallAccountManagementResponse } from '../../interfaces/account/IAccountChatboxResponse';
import { PaginatedApiResponse } from '../../interfaces/models/models/paginated-response.model';
import { IDocsStats } from '../../interfaces/account/IDocsStats';
import { IAccountDetails, IAccountDetailsIssues } from '../../interfaces/account/IAccountDetails';
import { IPaginationPayload } from '../../interfaces/shared/IPaginationPayload';
import {
    ITipsFeedbackAnalysisRequest,
    ITipsFeedbackItem,
    ITipsFeedbackMetaAnalysis,
    ITipsFeedbackMutationResponse,
    ITipsFeedbackRequest,
    ITipsFeedbackResolveRequest,
    ITipsFeedbackResponse,
    ITipsFeedbackStatusRequest,
} from '../../interfaces/tips-feedback/ITipsFeedback';
import { normalizeFeedbackDetails, normalizeFeedbackList, unwrapFeedbackResponse } from '../../utils/tips-feedback/response';
import {
    IComparisonBoardRequest,
    IComparisonBoardResponse,
    ICreateComparisonBoardRequest,
    IUpdateComparisonBoardStatusRequest,
} from '../../interfaces/comparison-boards/IComparisonBoards';

export interface ILeoChatFile {
    name: string;
    url?: string;
    link?: string;
    type?: string;
}

export type ILeoChatOption =
    | string
    | {
        key?: string | number;
        value?: string;
        label?: string;
        text?: string;
        message?: string;
        title?: string;
        name?: string;
        [key: string]: unknown;
    };

export interface ILeoChatSelectedClient {
    id: string;
    label: string;
    username?: string;
    companySub?: string;
    companyName?: string;
    name?: string;
}

export interface ILeoChatMessagePayload {
    question?: string;
    topicId?: string;
    stage?: string;
    client?: string;
}

export type ILeoChatColumn =
    | string
    | {
        key?: string;
        field?: string;
        name?: string;
        label?: string;
        header?: string;
        title?: string;
        value?: string;
        id?: string;
        [key: string]: unknown;
    };

export type ILeoChatResponseType = 'text' | 'table' | string;

export type ILeoChatStructuredText = string | Record<string, unknown>;

export interface ILeoChatMeta {
    [key: string]: unknown;
}

export interface ILeoChatResponse {
    _id?: string;
    agentMessageId?: string;
    topicId?: string;
    chatId?: string;
    topic?: string;
    message?: string;
    files?: ILeoChatFile[];
    options?: ILeoChatOption[];
    stage?: string;
    responseType?: ILeoChatResponseType;
    summary?: string;
    text?: ILeoChatStructuredText;
    rows?: Record<string, unknown>[];
    columns?: ILeoChatColumn[];
    meta?: ILeoChatMeta;
    createdAt?: string;
    metadata?: {
        messageId?: string;
        [key: string]: unknown;
    };
    details?: {
        title?: string;
        type?: string;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

const buildBillyPaginatedPutUrl = ({
    path,
    pageNumber,
    pageSize,
    sort,
}: {
    path: string;
    pageNumber?: number;
    pageSize?: number;
    sort?: string;
}) => {
    const params = new URLSearchParams();
    params.set('page', String(pageNumber || 1));
    params.set('size', String(pageSize || 10));
    if (sort) params.set('sort', sort);
    return `${path}?${params.toString()}`;
};

const buildComparisonBoardsUrl = ({
    pageNumber,
    pageSize,
    search,
    active,
}: {
    pageNumber?: number;
    pageSize?: number;
    search?: string;
    active?: boolean;
}) => {
    const params = new URLSearchParams();
    params.set('page', String(pageNumber || 1));
    params.set('size', String(pageSize || 10));
    params.set('search', search || '');
    if (typeof active === 'boolean') {
        params.set('active', String(active));
    }
    return `/billy/accounts/cloned?${params.toString()}`;
};

const buildDeviceInfoUrl = (path: string) => {
    const baseUrl = String(DEVICE_INFO_API_URL || '').trim().replace(/\/+$/, '');
    if (!baseUrl || baseUrl === 'undefined') return path;
    return `${baseUrl}${path}`;
};

const transformComparisonBoardsResponse = (response: any): IComparisonBoardResponse => {
    const payload = response?.data?.data || response?.data || response || {};
    const elements = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.elements)
            ? payload.elements
            : Array.isArray(payload)
                ? payload
                : [];
    const pageNumber = typeof payload?.page === 'number'
        ? payload.page
        : payload?.page?.number || payload?.page?.pageNumber;
    const totalPages = payload?.totalPages || payload?.page?.totalPages;

    return {
        elements,
        page: {
            number: pageNumber,
            page: pageNumber,
            pageNumber,
            size: payload?.size || payload?.page?.size,
            total: payload?.total || payload?.page?.total,
            totalElements: payload?.total || payload?.totalElements || payload?.page?.totalElements,
            totalPages,
            hasNextPage: typeof payload?.hasNextPage === 'boolean'
                ? payload.hasNextPage
                : typeof pageNumber === 'number' && typeof totalPages === 'number'
                    ? pageNumber < totalPages
                    : payload?.page?.hasNextPage,
        },
    };
};

export const chatboxApi = createApi({
    reducerPath: 'chatbox',
    baseQuery: deviceInfoAxiosQuery(),
    tagTypes: ['overall', 'statistics', 'tipsFeedback'],
    endpoints: (builder) => ({
        getEnhancedNarrativeReport: builder.mutation<unknown, EnhancedNarrativeRequest>({
            query: ({ property, auditScope, projectType, regenerate }) => ({
                url: '/billy/leo/reports/enhanced-narrative',
                method: 'POST',
                data: {
                    property,
                    ...(auditScope ? { auditScope } : {}),
                    ...(projectType ? { projectType } : {}),
                    ...(regenerate ? { regenerate: true } : {}),
                },
            }),
        }),
        overallAccountManagement: builder.mutation<
            { data: IOverallAccountManagementResponse[] },
            IAccountManagement
        >({
            query: (data) => ({
                url: `/chatbox/chats/account-management/overall`,
                method: 'PUT',
                data,
            }),
        }),
        accountManagementDetails: builder.mutation<
            PaginatedApiResponse<IAccountDetails>,
            IAccountManagementDetails
        >({
            query: (data) => ({
                url: `/chatbox/chats/account-management/details?page=${data.pageNumber || 1}&size=${data.pageSize || 10}`,
                method: 'PUT',
                data,
            }),
        }),
        accountManagementDetailsIssues: builder.mutation<
            PaginatedApiResponse<IAccountDetailsIssues>,
            IAccountManagementDetails
        >({
            query: (data) => ({
                url: `/chatbox/chats/issues/report-details?page=${data.pageNumber || 1}&size=${data.pageSize || 10}`,
                method: 'PUT',
                data,
            }),
        }),
        downloadsAccountDetails: builder.mutation<
            { data: { excelLink: string } },
            {
                hotelId: string,
                sourceName?: string
            }
        >({
            query: (data) => ({
                url: `/chatbox/chats/account-management/details-download`,
                method: 'PUT',
                data,
            }),
        }),
        downloadsAccountDetailsIssues: builder.mutation<
            { data: { excelLink: string } },
            {
                hotelId: string,
                sourceName?: string
            }
        >({
            query: (data) => ({
                url: `/chatbox/chats/issues/report-details/download`,
                method: 'PUT',
                data,
            }),
        }),
        productTeamStatistics: builder.mutation<
            PaginatedApiResponse<IDocsStats>,
            {
                hotelId: string;
                sourceName?: string;
            }
        >({
            query: (data) => ({
                url: `/chatbox/chats/product-team/statistics`,
                method: 'PUT',
                data,
            }),
        }),
        productTeamStatisticsDownload: builder.mutation<
            { data: { excelLink: string } },
            {
                hotelId: string;
                sourceName?: string;
            }
        >({
            query: (data) => ({
                url: `/chatbox/chats/product-team/statistics-download`,
                method: 'PUT',
                data,
            }),
        }),
        getAIModels: builder.query<{ data: any }, void>({
            query: () => ({
                url: `/billy/ai-search-engine/ai-models/all`,
                method: 'GET',
            })
        }),
        getUserAIModels: builder.query<{ data: any }, any>({
            query: (data: any) => ({
                url: `/billy/ai-search-engine/enabled-ai-models`,
                method: 'PUT',
                data: data
            })
        }),
        getAllUserConfigs: builder.query<{ data: any }, any>({
            query: (userId: string) => ({
                url: `/billy/ai-search-engine/all-configs?userId=${encodeURIComponent(userId)}`,
                method: 'GET',
            })
        }),
        getUsersNeedHelp: builder.mutation<
            any,
            IPaginationPayload
        >({
            query: (data) => ({
                url: buildBillyPaginatedPutUrl({
                    path: '/billy/need-help',
                    pageNumber: data?.pageNumber,
                    pageSize: data?.pageSize,
                    sort: typeof data?.sort === 'string'
                        ? data.sort
                        : undefined,
                }),
                method: 'PUT',
                data: data?.data || {},
            }),
        }),
        getTipsFeedback: builder.mutation<
            ITipsFeedbackResponse,
            ITipsFeedbackRequest
        >({
            query: (data) => ({
                url: buildBillyPaginatedPutUrl({
                    path: '/billy/tips-feedback',
                    pageNumber: data?.pageNumber,
                    pageSize: data?.pageSize,
                    sort: data?.sort,
                }),
                method: 'PUT',
                data: data?.data || {},
            }),
            transformResponse: normalizeFeedbackList,
        }),
        getTipsFeedbackDetails: builder.query<ITipsFeedbackItem, string>({
            query: (feedbackId) => ({
                url: `/billy/tips-feedback/${encodeURIComponent(feedbackId)}`,
                method: 'GET',
            }),
            transformResponse: normalizeFeedbackDetails,
            providesTags: (_result, _error, id) => [{ type: 'tipsFeedback', id }],
        }),
        getTipsFeedbackMetaAnalysis: builder.mutation<
            ITipsFeedbackMetaAnalysis,
            ITipsFeedbackAnalysisRequest
        >({
            query: (data) => ({
                url: `/billy/tips-feedback/meta-analysis?sort=${encodeURIComponent(data.sort || 'date,desc')}`,
                method: 'PUT',
                data: data?.data || {},
            }),
            transformResponse: (response: unknown) => unwrapFeedbackResponse(response) as ITipsFeedbackMetaAnalysis,
        }),
        updateTipsFeedbackStatus: builder.mutation<ITipsFeedbackMutationResponse, ITipsFeedbackStatusRequest>({
            query: ({ feedbackId, ...data }) => ({
                url: `/billy/tips-feedback/${encodeURIComponent(feedbackId)}/status`,
                method: 'PUT',
                data,
            }),
            transformResponse: (response: unknown) => unwrapFeedbackResponse(response) as ITipsFeedbackMutationResponse,
            invalidatesTags: (_result, error, { feedbackId }) =>
                error ? [] : [{ type: 'tipsFeedback', id: feedbackId }],
        }),
        resolveTipsFeedback: builder.mutation<ITipsFeedbackMutationResponse, ITipsFeedbackResolveRequest>({
            query: ({ feedbackId, ...data }) => ({
                url: `/billy/tips-feedback/${encodeURIComponent(feedbackId)}/resolve`,
                method: 'PUT',
                data,
            }),
            transformResponse: (response: unknown) => unwrapFeedbackResponse(response) as ITipsFeedbackMutationResponse,
            // Resolution is persisted before regeneration, which may fail later.
            invalidatesTags: (_result, _error, { feedbackId }) => [{ type: 'tipsFeedback', id: feedbackId }],
        }),
        getComparisonBoards: builder.mutation<
            IComparisonBoardResponse,
            IComparisonBoardRequest
        >({
            query: (data) => ({
                url: buildComparisonBoardsUrl({
                    pageNumber: data?.pageNumber,
                    pageSize: data?.pageSize,
                    search: data?.data?.search,
                    active: data?.data?.active,
                }),
                method: 'GET',
            }),
            transformResponse: transformComparisonBoardsResponse,
        }),
        createComparisonBoard: builder.mutation<
            { success?: boolean; message?: string; data?: unknown },
            ICreateComparisonBoardRequest
        >({
            query: (data) => ({
                url: '/billy/leo/clone',
                method: 'PUT',
                data,
            }),
        }),
        updateComparisonBoardStatus: builder.mutation<
            { success?: boolean; message?: string; data?: unknown },
            IUpdateComparisonBoardStatusRequest
        >({
            query: (data) => ({
                url: '/billy/accounts/cloned/active',
                method: 'PUT',
                data,
            }),
        }),
        sendLeoChatMessage: builder.mutation<
            ILeoChatResponse,
            ILeoChatMessagePayload
        >({
            query: (data) => ({
                url: buildDeviceInfoUrl('/dom/leo/chat/intelligence'),
                method: 'POST',
                data,
            }),
        }),
        uploadLeoChatFiles: builder.mutation<ILeoChatFile[], FormData>({
            query: (data) => ({
                url: '/files/upload-chat-files',
                method: 'POST',
                data,
            }),
            transformResponse: (response: any): ILeoChatFile[] => {
                const payload = response?.data || response;
                const files = Array.isArray(payload) ? payload : [];

                return files.map((file: any) => ({
                    name: file?.name || file?.originalname || 'Uploaded file',
                    url: file?.url || file?.link,
                    link: file?.link || file?.url,
                    type: file?.type,
                }));
            },
        }),
    }),
});

export const {
    useGetEnhancedNarrativeReportMutation,
    useAccountManagementDetailsIssuesMutation,
    useDownloadsAccountDetailsIssuesMutation,
    useOverallAccountManagementMutation,
    useAccountManagementDetailsMutation,
    useDownloadsAccountDetailsMutation,
    useProductTeamStatisticsDownloadMutation,
    useProductTeamStatisticsMutation,
    useGetAIModelsQuery,
    useGetUserAIModelsQuery,
    useGetAllUserConfigsQuery,
    useLazyGetAllUserConfigsQuery,
    useGetUsersNeedHelpMutation,
    useGetTipsFeedbackMutation,
    useGetTipsFeedbackDetailsQuery,
    useGetTipsFeedbackMetaAnalysisMutation,
    useUpdateTipsFeedbackStatusMutation,
    useResolveTipsFeedbackMutation,
    useGetComparisonBoardsMutation,
    useCreateComparisonBoardMutation,
    useUpdateComparisonBoardStatusMutation,
    useSendLeoChatMessageMutation,
    useUploadLeoChatFilesMutation,
} = chatboxApi;
